import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, AlertTriangle, Calendar, CheckCircle2, HelpCircle } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
// PAGE 15: Proof tiles
export function Page15() {
  const proofs = [{
    brand: 'Next',
    metric: '6 weeks',
    label: 'to launch across chat, voice, WhatsApp',
    relevance: 'Speed to value'
  }, {
    brand: 'Ramp',
    metric: '90%',
    label: 'case resolution',
    relevance: 'Deflection + cost reduction'
  }, {
    brand: 'SoFi',
    metric: '+33',
    label: 'chat-contained NPS (61% containment)',
    relevance: 'Quality + scale'
  }, {
    brand: 'Wilson',
    metric: '77%',
    label: 'resolution rate',
    relevance: 'Self-serve quality'
  }, {
    brand: 'AOL',
    metric: '64%',
    label: 'case resolution',
    relevance: 'Heritage brand modernisation'
  }, {
    brand: 'Rocket Mortgage',
    metric: '4x',
    label: 'conversion when AI + human',
    relevance: 'Hybrid works'
  }];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-6xl mx-auto w-full" data-id="element-530">
      <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-10 text-center" data-id="element-531">
        Proven at Enterprise Scale
      </motion.h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-532">
        {proofs.map((p, i) => <motion.div key={i} variants={fadeUp} className="bg-white p-6 rounded-2xl sierra-shadow border border-gray-100 flex flex-col h-full" data-id="element-533">
            <div className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-534">
              {p.brand}
            </div>
            <div className="text-4xl font-bold text-sierra-blue mb-2" data-id="element-535">
              {p.metric}
            </div>
            <div className="text-gray-700 font-medium mb-4 flex-1" data-id="element-536">
              {p.label}
            </div>
            <div className="pt-4 border-t border-gray-100 text-xs text-gray-500 font-semibold uppercase" data-id="element-537">
              Why it matters:{' '}
              <span className="text-sierra-dark" data-id="element-538">{p.relevance}</span>
            </div>
          </motion.div>)}
      </div>
    </motion.div>;
}
// PAGE 16: Trust & reliability
export function Page16() {
  const features = ['PII masked/redacted by default.', 'Confirmation gates before irreversible actions.', "Least-privilege tool access (only what's needed).", 'Grounding in policy/knowledge and tool outputs.', 'Escalation triggers when confidence or risk is high.', 'Audit logging for every action and decision.'];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-5xl mx-auto w-full" data-id="element-539">
      <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-10 text-center" data-id="element-540">
        Appendix: Enterprise-Grade Trust
      </motion.h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-541">
        <motion.div variants={fadeUp} className="space-y-4" data-id="element-542">
          <h3 className="text-lg font-semibold mb-6 flex items-center gap-2" data-id="element-543">
            <ShieldCheck className="w-5 h-5 text-sierra-blue" data-id="element-544" /> Default Secure
          </h3>
          {features.map((f, i) => <div key={i} className="flex items-start gap-3" data-id="element-545">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" data-id="element-546" />
              <p className="text-gray-700" data-id="element-547">{f}</p>
            </div>)}
        </motion.div>

        <motion.div variants={fadeUp} className="space-y-6" data-id="element-548">
          <div className="bg-red-50 border border-red-100 p-6 rounded-2xl" data-id="element-549">
            <h3 className="text-red-800 font-semibold mb-3 flex items-center gap-2" data-id="element-550">
              <AlertTriangle className="w-5 h-5" data-id="element-551" /> Abuse Case Handling
            </h3>
            <ul className="space-y-3 text-sm text-red-900" data-id="element-552">
              <li data-id="element-553">
                <strong data-id="element-554">Prompt Injection:</strong> ("ignore policy") → Blocked
                by supervisor model.
              </li>
              <li data-id="element-555">
                <strong data-id="element-556">Social Engineering:</strong> ("activate eSIM on another
                account") → Blocked by strict auth + entitlement checks.
              </li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-100 p-6 rounded-2xl" data-id="element-557">
            <h3 className="text-amber-800 font-semibold mb-3 flex items-center gap-2" data-id="element-558">
              <Lock className="w-5 h-5" data-id="element-559" /> Safe Failure Mode
            </h3>
            <p className="text-sm text-amber-900 leading-relaxed" data-id="element-560">
              <strong data-id="element-561">API Outage Scenario:</strong> Degrade gracefully. Agent
              informs user, creates a ticket in ServiceNow, schedules a
              callback, and escalates to human queue.
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>;
}
// PAGE 17: Rollout plan
export function Page17() {
  const phases = [{
    weeks: 'Weeks 0–2',
    title: 'Launch & Learn',
    desc: 'Launch eSIM journey in chat for 1 region; measure containment + CSAT.'
  }, {
    weeks: 'Weeks 3–6',
    title: 'Expand & Refine',
    desc: 'Expand to more devices/carriers; deeper troubleshooting; refine guardrails.'
  }, {
    weeks: 'Weeks 6–10',
    title: 'Scale Channels',
    desc: 'Add broadband journey + proactive updates; expand channels (voice/WhatsApp).'
  }, {
    weeks: 'Ongoing',
    title: 'Iterate',
    desc: 'Simulations regression suite + insights-driven iteration.'
  }];
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto w-full" data-id="element-562">
      <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-563">
        <h2 className="text-3xl font-bold text-sierra-dark mb-4" data-id="element-564">
          Appendix: Rollout Plan
        </h2>
        <p className="text-xl text-gray-500" data-id="element-565">
          Start small, prove value, expand safely.
        </p>
      </motion.div>

      <div className="relative border-l-2 border-sierra-blue/20 ml-4 md:ml-8 space-y-10" data-id="element-566">
        {phases.map((phase, i) => <motion.div key={i} variants={fadeUp} className="relative pl-8" data-id="element-567">
            <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-sierra-blue border-4 border-white shadow-sm" data-id="element-568" />
            <div className="bg-white p-6 rounded-2xl sierra-shadow border border-gray-100" data-id="element-569">
              <div className="flex items-center gap-3 mb-2" data-id="element-570">
                <span className="px-3 py-1 bg-blue-50 text-sierra-blue text-xs font-bold rounded-full uppercase tracking-wider" data-id="element-571">
                  {phase.weeks}
                </span>
                <h3 className="text-lg font-semibold text-sierra-dark" data-id="element-572">
                  {phase.title}
                </h3>
              </div>
              <p className="text-gray-600" data-id="element-573">{phase.desc}</p>
            </div>
          </motion.div>)}
      </div>
    </motion.div>;
}
// PAGE 18: Closing
export function Page18() {
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto w-full" data-id="element-574">
      <motion.h2 variants={fadeUp} className="text-3xl font-bold mb-12 text-center" data-id="element-575">
        Closing
      </motion.h2>
      <motion.div variants={fadeUp} className="space-y-10 bg-white p-10 rounded-3xl sierra-shadow border border-gray-100" data-id="element-576">
        <div data-id="element-577">
          <h3 className="text-xl font-bold mb-4 text-sierra-dark border-b border-gray-100 pb-2" data-id="element-578">
            Why Me
          </h3>
          <ul className="space-y-3 text-gray-600" data-id="element-579">
            <li className="flex items-start gap-2" data-id="element-580">
              <span className="text-sierra-blue mt-1" data-id="element-581">•</span> I create momentum
              in complex deals by turning uncertainty into clear technical
              motions.
            </li>
            <li className="flex items-start gap-2" data-id="element-582">
              <span className="text-sierra-blue mt-1" data-id="element-583">•</span> I build
              repeatable SE systems so teams scale beyond heroics.
            </li>
            <li className="flex items-start gap-2" data-id="element-584">
              <span className="text-sierra-blue mt-1" data-id="element-585">•</span> Comfortable in
              high-stakes rooms: exec storytelling + technical depth.
            </li>
          </ul>
        </div>
        <div data-id="element-586">
          <h3 className="text-xl font-bold mb-4 text-sierra-dark border-b border-gray-100 pb-2" data-id="element-587">
            Why Sierra
          </h3>
          <ul className="space-y-3 text-gray-600" data-id="element-588">
            <li className="flex items-start gap-2" data-id="element-589">
              <span className="text-sierra-blue mt-1" data-id="element-590">•</span> Values match how
              I work: trust, customer obsession, craftsmanship.
            </li>
            <li className="flex items-start gap-2" data-id="element-591">
              <span className="text-sierra-blue mt-1" data-id="element-592">•</span> Excited by
              outcome-driven agents that take action safely and measurably.
            </li>
          </ul>
        </div>
      </motion.div>
    </motion.div>;
}
// PAGE 19: Q&A
export function Page19() {
  return <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto w-full text-center" data-id="element-593">
      <motion.div variants={fadeUp} className="mb-12" data-id="element-594">
        <HelpCircle className="w-16 h-16 text-sierra-blue mx-auto mb-6" data-id="element-595" />
        <h1 className="text-6xl font-bold text-sierra-dark mb-6" data-id="element-596">Questions?</h1>
        <p className="text-2xl text-gray-500 font-light" data-id="element-597">
          I'd love to hear what's on your mind.
        </p>
      </motion.div>
    </motion.div>;
}